import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, Cell, Legend, LineChart, Line, ComposedChart, Area,
} from 'recharts';
import {
  AlertTriangle, Shield, Home, TrendingDown, Info, Building2, CheckCircle2,
  HelpCircle, DollarSign, Percent, TrendingUp, ArrowUpRight, ArrowDownRight,
  Calendar, PiggyBank,
} from 'lucide-react';
import {
  generateEscrowTimeline,
  type EscrowTimelineInput,
  type ExemptionProfile,
  type EscrowTimelineResult,
} from '@/engine/tax/escrowTimelineEngine';
import type { EnhancedUnifiedFormData } from '@/validation/enhancedUnifiedSchemas';
import type { MortgageData } from '@/types/calculator';

interface EscrowTimelineProps {
  formData?: EnhancedUnifiedFormData;
  mortgageData: MortgageData;
}

const phaseColors: Record<string, string> = {
  unimproved: 'hsl(var(--muted-foreground))',
  'improved-no-exemptions': 'hsl(var(--destructive))',
  'improved-with-exemptions': 'hsl(var(--primary))',
};

const phaseBadgeVariants: Record<string, 'secondary' | 'destructive' | 'default'> = {
  unimproved: 'secondary',
  'improved-no-exemptions': 'destructive',
  'improved-with-exemptions': 'default',
};

// ─── Inline Tooltip Helper ───────────────────────────────────
const InfoTip: React.FC<{ content: string; side?: 'top' | 'right' | 'bottom' | 'left' }> = ({
  content, side = 'top',
}) => (
  <TooltipProvider>
    <UITooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <button type="button" className="inline-flex items-center ml-1 text-muted-foreground hover:text-foreground transition-colors">
          <HelpCircle className="w-3.5 h-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent side={side} className="max-w-[260px] p-2.5 text-xs leading-relaxed" sideOffset={6}>
        {content}
      </TooltipContent>
    </UITooltip>
  </TooltipProvider>
);

// ─── Custom Recharts Tooltip ─────────────────────────────────
const EscrowChartTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  const data = payload[0]?.payload;
  if (!data) return null;
  return (
    <div className="bg-card border border-border rounded-lg p-3 shadow-lg text-xs space-y-1.5">
      <p className="font-semibold text-sm text-foreground">{label}</p>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
        <span className="text-muted-foreground">Monthly Escrow:</span>
        <span className="font-medium text-foreground">${data.monthlyEscrow?.toLocaleString()}</span>
        {data.withoutExemptions > 0 && data.withoutExemptions !== data.monthlyEscrow && (
          <>
            <span className="text-muted-foreground">Without Exemptions:</span>
            <span className="font-medium text-destructive">${data.withoutExemptions?.toLocaleString()}</span>
          </>
        )}
        {data.savings > 0 && (
          <>
            <span className="text-muted-foreground">Monthly Savings:</span>
            <span className="font-medium text-primary">${data.savings?.toLocaleString()}</span>
          </>
        )}
        <span className="text-muted-foreground">Assessed Value:</span>
        <span className="font-medium">${data.assessedValue?.toLocaleString()}</span>
        <span className="text-muted-foreground">Effective Rate:</span>
        <span className="font-medium">{data.effectiveRate}%</span>
        {data.yoyChange !== 0 && (
          <>
            <span className="text-muted-foreground">YoY Change:</span>
            <span className={`font-medium ${data.yoyChange > 0 ? 'text-destructive' : 'text-primary'}`}>
              {data.yoyChange > 0 ? '+' : ''}${data.yoyChange}/mo
            </span>
          </>
        )}
      </div>
      <p className="text-muted-foreground italic pt-1 border-t border-border">
        {data.phase === 'unimproved' ? 'Land-only assessment during construction' :
         data.phase === 'improved-no-exemptions' ? 'Full value — file homestead ASAP' :
         'Exemptions reducing your tax burden'}
      </p>
    </div>
  );
};

const SavingsChartTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  const data = payload[0]?.payload;
  return (
    <div className="bg-card border border-border rounded-lg p-3 shadow-lg text-xs space-y-1">
      <p className="font-semibold text-sm text-foreground">{label}</p>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
        <span className="text-muted-foreground">Cumulative Tax Paid:</span>
        <span className="font-medium">${data.cumulativeTax?.toLocaleString()}</span>
        <span className="text-muted-foreground">Cumulative Savings:</span>
        <span className="font-medium text-primary">${data.cumulativeSavings?.toLocaleString()}</span>
      </div>
    </div>
  );
};

// ─── Exemption Tooltip Descriptions ──────────────────────────
const EXEMPTION_TIPS: Record<string, string> = {
  newConstruction: 'During construction, only the land is assessed for property tax. After completion, the full improved value is assessed — often causing a big jump in escrow.',
  landValue: 'The percentage of the total home price attributable to the land alone. Typical range is 15-35%. Higher land value = higher taxes during construction.',
  homestead: 'Texas homestead exemption removes $100,000 from your assessed value for general taxes and an additional $100,000 for school district taxes. You must file after January 1 of the year following purchase.',
  over65: 'If you or your spouse are 65+, you get an additional $10,000 exemption and your school district taxes are frozen (capped) at the amount in the year you turn 65 or qualify.',
  disabled: 'A $10,000 additional exemption for disabled persons, plus a school tax ceiling freeze. Cannot be combined with the Over-65 exemption — the higher benefit applies.',
  spouse: 'If your spouse who received an Over-65 or Disabled exemption passes away, you can maintain their tax ceiling. You must apply within 2 years.',
};

export const EscrowTimeline: React.FC<EscrowTimelineProps> = ({ formData, mortgageData }) => {
  const [homestead, setHomestead] = useState(true);
  const [over65, setOver65] = useState(false);
  const [disabledPerson, setDisabledPerson] = useState(false);
  const [survivingSpouse, setSurvivingSpouse] = useState(false);
  const [isNewConstruction, setIsNewConstruction] = useState(true);
  const [landValuePct, setLandValuePct] = useState(25);

  const vaDisabilityStatus = mortgageData.vaDisabilityStatus as 'none' | 'partial' | 'full' | null;
  const vaRating = mortgageData.vaDisabilityRatingPercentage ?? 0;
  const veteranAge = mortgageData.veteranAge ?? null;

  const exemptions: ExemptionProfile = useMemo(() => ({
    homestead, over65, disabledPerson, survivingSpouse,
    vaDisabilityStatus: vaDisabilityStatus && vaDisabilityStatus !== 'none' ? vaDisabilityStatus : 'none',
    vaDisabilityRating: vaRating,
    veteranAge,
  }), [homestead, over65, disabledPerson, survivingSpouse, vaDisabilityStatus, vaRating, veteranAge]);

  const homePrice = mortgageData.homePrice || 400000;
  const taxRate = formData?.propertyLoan?.propertyTaxRate
    ? formData.propertyLoan.propertyTaxRate / 100
    : 0.02;

  const input: EscrowTimelineInput = useMemo(() => ({
    homePrice,
    landValuePercentage: landValuePct,
    propertyTaxRate: taxRate,
    state: mortgageData.state || 'TX',
    exemptions,
    constructionPeriodMonths: isNewConstruction ? 8 : 0,
    annualAppreciation: 0.03,
  }), [homePrice, landValuePct, taxRate, mortgageData.state, exemptions, isNewConstruction]);

  const timeline: EscrowTimelineResult = useMemo(() => generateEscrowTimeline(input), [input]);

  // Chart data for main escrow chart
  const chartData = timeline.years.map(y => ({
    name: y.label,
    monthlyEscrow: y.monthlyEscrow,
    withoutExemptions: y.monthlyEscrowWithoutExemptions,
    savings: y.monthlyEscrowWithoutExemptions - y.monthlyEscrow,
    phase: y.phase,
    assessedValue: y.assessedValue,
    effectiveRate: y.effectiveTaxRate,
    yoyChange: y.yoyChange,
  }));

  // Chart data for cumulative savings
  const savingsChartData = timeline.years.map(y => ({
    name: y.label,
    cumulativeTax: y.cumulativeTaxPaid,
    cumulativeSavings: y.cumulativeSavings,
  }));

  const maxExemptionsYear = timeline.years.reduce((max, y) =>
    y.totalExemptions > max.totalExemptions ? y : max, timeline.years[0]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="border-border">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Building2 className="w-5 h-5 text-primary" />
            Escrow & Tax Timeline
            <InfoTip content="This tool models how your property taxes and monthly escrow payments evolve from construction through homestead filing. It accounts for unimproved vs. improved assessed values and all applicable Texas exemptions." />
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            See how your property taxes and escrow payments change over time — from construction through homestead filing.
          </p>
        </CardHeader>
      </Card>

      {/* Escrow Shock Warning */}
      {timeline.escrowShockWarning && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {timeline.escrowShockWarning}
            <InfoTip content="'Escrow shock' happens when your lender re-analyzes your escrow account after the county reassesses your home at its full improved value. Your monthly payment can jump hundreds of dollars. Filing for homestead exemption ASAP helps soften this." side="bottom" />
          </AlertDescription>
        </Alert>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {timeline.summary.unimprovedMonthly > 0 && (
          <Card className="border-border">
            <CardContent className="pt-4 pb-3 text-center">
              <p className="text-xs text-muted-foreground uppercase tracking-wide flex items-center justify-center gap-1">
                During Construction
                <InfoTip content="While the home is being built, only the raw land value is taxed. This results in much lower escrow payments that will increase significantly once the home is completed." />
              </p>
              <p className="text-2xl font-bold text-muted-foreground">${timeline.summary.unimprovedMonthly.toLocaleString()}/mo</p>
              <p className="text-xs text-muted-foreground">Land value only</p>
            </CardContent>
          </Card>
        )}
        <Card className="border-border">
          <CardContent className="pt-4 pb-3 text-center">
            <p className="text-xs text-muted-foreground uppercase tracking-wide flex items-center justify-center gap-1">
              Before Exemptions
              <InfoTip content="After the home is completed, the county will assess it at the full improved value. Until you file for homestead (typically the January after closing), you pay the full tax rate with no exemptions." />
            </p>
            <p className="text-2xl font-bold text-destructive">${timeline.summary.improvedNoExemptionsMonthly.toLocaleString()}/mo</p>
            <p className="text-xs text-muted-foreground">Full assessed value</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-4 pb-3 text-center">
            <p className="text-xs text-muted-foreground uppercase tracking-wide flex items-center justify-center gap-1">
              With Exemptions
              <InfoTip content="Once your homestead and other exemptions take effect (usually the year after filing), your taxable value drops and your escrow payments decrease accordingly." />
            </p>
            <p className="text-2xl font-bold text-primary">${timeline.summary.improvedWithExemptionsMonthly.toLocaleString()}/mo</p>
            <p className="text-xs text-muted-foreground">After homestead filed</p>
            {timeline.summary.maxEscrowChange !== 0 && (
              <p className="text-xs mt-1 flex items-center justify-center gap-0.5">
                {timeline.summary.maxEscrowChange > 0 ? (
                  <ArrowUpRight className="w-3 h-3 text-destructive" />
                ) : (
                  <ArrowDownRight className="w-3 h-3 text-primary" />
                )}
                <span className={timeline.summary.maxEscrowChange > 0 ? 'text-destructive' : 'text-primary'}>
                  Peak change: {timeline.summary.maxEscrowChange > 0 ? '+' : ''}${timeline.summary.maxEscrowChange}/mo
                </span>
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-border">
          <CardContent className="pt-3 pb-2 text-center">
            <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
              <Percent className="w-3 h-3" /> Effective Rate (Yr 10)
              <InfoTip content="Your effective tax rate is the actual tax paid divided by assessed value. With exemptions, this is lower than the stated property tax rate." />
            </div>
            <p className="text-lg font-bold">{timeline.years[timeline.years.length - 1]?.effectiveTaxRate}%</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-3 pb-2 text-center">
            <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
              <DollarSign className="w-3 h-3" /> 10-Year Total Tax
              <InfoTip content="The total amount of property tax you'll pay over the 10-year modeled period, after all exemptions are applied." />
            </div>
            <p className="text-lg font-bold">${timeline.years[timeline.years.length - 1]?.cumulativeTaxPaid.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-3 pb-2 text-center">
            <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
              <PiggyBank className="w-3 h-3" /> Max Exemptions
              <InfoTip content="The highest total exemption amount applied in any single year. This is the combined value removed from your assessed value before calculating taxes." />
            </div>
            <p className="text-lg font-bold">${maxExemptionsYear.totalExemptions.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-3 pb-2 text-center">
            <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
              <Calendar className="w-3 h-3" /> Exemptions Start
              <InfoTip content="The year when your exemptions first take effect. For new construction, this is typically Year 2 (the January after your first full year in the home)." />
            </div>
            <p className="text-lg font-bold">{timeline.years[timeline.firstYearWithExemptions]?.label || 'Year 2'}</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Chart: Escrow with/without exemptions */}
      <Card className="border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            Monthly Escrow Over Time
            <InfoTip content="The colored bars show your actual monthly escrow. The semi-transparent bars show what you'd pay without any exemptions. The difference is your monthly savings from exemptions." />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" tickFormatter={v => `$${v}`} />
              <RechartsTooltip content={<EscrowChartTooltip />} />
              <Bar dataKey="withoutExemptions" radius={[4, 4, 0, 0]} fill="hsl(var(--muted-foreground))" opacity={0.2} name="Without Exemptions" />
              <Bar dataKey="monthlyEscrow" radius={[4, 4, 0, 0]} name="With Exemptions">
                {chartData.map((entry, index) => (
                  <Cell key={index} fill={phaseColors[entry.phase]} />
                ))}
              </Bar>
            </ComposedChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-4 mt-3 justify-center text-xs">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: phaseColors.unimproved }} />
              <span className="text-muted-foreground">Unimproved (Land Only)</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: phaseColors['improved-no-exemptions'] }} />
              <span className="text-muted-foreground">Full Value (No Exemptions)</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: phaseColors['improved-with-exemptions'] }} />
              <span className="text-muted-foreground">Exemptions Applied</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-sm bg-muted-foreground/20" />
              <span className="text-muted-foreground">What You'd Pay (No Exemptions)</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cumulative Savings Chart */}
      <Card className="border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <TrendingDown className="w-4 h-4 text-primary" />
            Cumulative Tax & Savings
            <InfoTip content="Track your total taxes paid and total savings from exemptions over the 10-year period. The green area shows how much your exemptions have saved you in total." />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={savingsChartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
              <RechartsTooltip content={<SavingsChartTooltip />} />
              <Area
                type="monotone"
                dataKey="cumulativeSavings"
                fill="hsl(var(--primary))"
                fillOpacity={0.15}
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                name="Cumulative Savings"
              />
              <Line
                type="monotone"
                dataKey="cumulativeTax"
                stroke="hsl(var(--muted-foreground))"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={{ r: 3 }}
                name="Cumulative Tax Paid"
              />
            </ComposedChart>
          </ResponsiveContainer>
          <div className="flex gap-6 mt-3 justify-center text-xs">
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-0.5 bg-muted-foreground" style={{ borderTop: '2px dashed' }} />
              <span className="text-muted-foreground">Total Tax Paid</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-3 rounded-sm bg-primary/20 border border-primary" />
              <span className="text-muted-foreground">Cumulative Savings</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Controls */}
      <Card className="border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Exemption Settings
            <InfoTip content="Toggle different Texas property tax exemptions to see how they affect your escrow payments. Each exemption reduces your assessed (taxable) value by a fixed dollar amount." />
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="new-construction" className="text-sm flex items-center gap-1">
              New Construction
              <InfoTip content={EXEMPTION_TIPS.newConstruction} />
            </Label>
            <Switch id="new-construction" checked={isNewConstruction} onCheckedChange={setIsNewConstruction} />
          </div>

          {isNewConstruction && (
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground flex items-center gap-1">
                Land Value (% of home price): {landValuePct}%
                <InfoTip content={EXEMPTION_TIPS.landValue} />
              </Label>
              <Slider
                value={[landValuePct]}
                onValueChange={([v]) => setLandValuePct(v)}
                min={10} max={50} step={5}
              />
            </div>
          )}

          <Separator />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex items-center justify-between">
              <Label htmlFor="homestead" className="text-sm flex items-center gap-1">
                Homestead Exemption
                <InfoTip content={EXEMPTION_TIPS.homestead} />
              </Label>
              <Switch id="homestead" checked={homestead} onCheckedChange={setHomestead} />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="over65" className="text-sm flex items-center gap-1">
                Over-65 Exemption
                <InfoTip content={EXEMPTION_TIPS.over65} />
              </Label>
              <Switch id="over65" checked={over65} onCheckedChange={setOver65} />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="disabled" className="text-sm flex items-center gap-1">
                Disabled Person
                <InfoTip content={EXEMPTION_TIPS.disabled} />
              </Label>
              <Switch id="disabled" checked={disabledPerson} onCheckedChange={setDisabledPerson} />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="spouse" className="text-sm flex items-center gap-1">
                Surviving Spouse
                <InfoTip content={EXEMPTION_TIPS.spouse} />
              </Label>
              <Switch id="spouse" checked={survivingSpouse} onCheckedChange={setSurvivingSpouse} />
            </div>
          </div>

          {vaDisabilityStatus && vaDisabilityStatus !== 'none' && (
            <div className="flex items-center gap-2 p-2 rounded bg-muted/50">
              <CheckCircle2 className="w-4 h-4 text-primary" />
              <span className="text-sm">
                VA Disability ({vaRating}% — {vaDisabilityStatus}) auto-applied from calculator
              </span>
              <InfoTip content="Your VA disability status was imported from the calculator inputs. 100% disabled veterans receive a full property tax exemption in Texas. Partial disability ratings receive tiered exemptions ($5K–$12K based on rating)." />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Year-by-Year Detail */}
      <Card className="border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            Year-by-Year Breakdown
            <InfoTip content="A detailed view of each year's tax assessment, showing assessed value, exemptions applied, taxable value, annual tax, and year-over-year changes." />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {timeline.years.map((year) => (
              <div key={year.year} className="border border-border rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm">{year.label}</span>
                    <Badge variant={phaseBadgeVariants[year.phase]}>
                      {year.phase === 'unimproved' ? 'Land Only' :
                       year.phase === 'improved-no-exemptions' ? 'No Exemptions' :
                       'Exemptions Applied'}
                    </Badge>
                    {year.yoyChange !== 0 && (
                      <Badge variant={year.yoyChange > 0 ? 'destructive' : 'default'} className="text-[10px]">
                        {year.yoyChange > 0 ? (
                          <ArrowUpRight className="w-2.5 h-2.5 mr-0.5" />
                        ) : (
                          <ArrowDownRight className="w-2.5 h-2.5 mr-0.5" />
                        )}
                        {year.yoyChange > 0 ? '+' : ''}${year.yoyChange}/mo ({year.yoyChangePct > 0 ? '+' : ''}{year.yoyChangePct}%)
                      </Badge>
                    )}
                  </div>
                  <span className="font-bold text-lg">${year.monthlyEscrow.toLocaleString()}/mo</span>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs text-muted-foreground">
                  <div>
                    <span className="block font-medium flex items-center gap-0.5">
                      Assessed
                      <InfoTip content="The value the county appraisal district assigns to your property. For new construction, this is land-only during building and full value after completion." />
                    </span>
                    ${year.assessedValue.toLocaleString()}
                  </div>
                  <div>
                    <span className="block font-medium flex items-center gap-0.5">
                      Exemptions
                      <InfoTip content="Total dollar amount removed from your assessed value by all applicable exemptions before calculating taxes." />
                    </span>
                    ${year.totalExemptions.toLocaleString()}
                  </div>
                  <div>
                    <span className="block font-medium flex items-center gap-0.5">
                      Taxable
                      <InfoTip content="Assessed value minus exemptions. This is the value your tax rate is actually applied to." />
                    </span>
                    ${year.taxableValue.toLocaleString()}
                  </div>
                  <div>
                    <span className="block font-medium flex items-center gap-0.5">
                      Annual Tax
                      <InfoTip content="Total property tax for the year, calculated as taxable value × tax rate." />
                    </span>
                    ${year.annualTax.toLocaleString()}
                  </div>
                  <div>
                    <span className="block font-medium flex items-center gap-0.5">
                      Eff. Rate
                      <InfoTip content="Your effective tax rate — actual tax paid divided by assessed value. Lower than the stated rate when exemptions are applied." />
                    </span>
                    {year.effectiveTaxRate}%
                  </div>
                </div>

                {year.notes.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {year.notes.map((note, i) => (
                      <p key={i} className="text-xs text-muted-foreground flex items-start gap-1">
                        <Info className="w-3 h-3 mt-0.5 shrink-0" />
                        {note}
                      </p>
                    ))}
                  </div>
                )}

                {year.exemptionBreakdown.filter(e => e.appliesInYear && e.amount > 0).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {year.exemptionBreakdown
                      .filter(e => e.appliesInYear && e.amount > 0)
                      .map((e, i) => (
                        <TooltipProvider key={i}>
                          <UITooltip delayDuration={200}>
                            <TooltipTrigger asChild>
                              <div>
                                <Badge variant="secondary" className="text-[10px] cursor-help">
                                  {e.name}: -${e.amount.toLocaleString()}
                                </Badge>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-[240px] p-2 text-xs" sideOffset={4}>
                              {e.description}
                            </TooltipContent>
                          </UITooltip>
                        </TooltipProvider>
                      ))}
                  </div>
                )}

                {year.taxCeiling && (
                  <p className="text-xs text-primary mt-1 flex items-center gap-1">
                    <Shield className="w-3 h-3" />
                    Tax ceiling: ${year.taxCeiling.toLocaleString()}/yr
                    <InfoTip content="Your school district taxes are frozen at this ceiling amount. Even if your home value increases, your school taxes won't exceed this level (though other jurisdictions may still increase)." />
                  </p>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Total Savings */}
      <Card className="border-border bg-primary/5">
        <CardContent className="pt-4 pb-3">
          <div className="flex items-center gap-3">
            <TrendingDown className="w-6 h-6 text-primary" />
            <div>
              <p className="text-sm font-medium flex items-center gap-1">
                Estimated 10-Year Tax Savings from Exemptions
                <InfoTip content="The total dollar amount saved over 10 years by having exemptions applied versus paying the full unexempted tax rate. Filing for homestead as soon as possible maximizes this savings." />
              </p>
              <p className="text-2xl font-bold text-primary">${timeline.totalSavingsFromExemptions.toLocaleString()}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
